import { SharingMinistryType, SubmissionType } from "../../types/supabase";
import { ExpenseModel, isCompleteExpense } from "../../reduxToolkit/expensesSlice";
import { IncidentModel } from "../../reduxToolkit/incidentsSlice";
import { SubmissionModel, isSubmitted } from "../../reduxToolkit/submissionsSlice";
import { formatISO, addMonths } from "date-fns";
import { parseDateInTimeZone } from "../formatDateInTimeZone";
import { Customizations } from "../../reduxToolkit/selectors/customizations";
import { Decimal } from 'decimal.js';
import { getCurrentLimits, getSmiPlanLimits } from "./limits";
import { SmiPlan } from "../../types/supabase";
import { present } from "../util/present";

/**
 * The member's sharing ministry identity + tier data, as consumed by the
 * submission-type rules. Built by `selectMinistryContext` on the web; the PHP
 * mirror reads the equivalent fields off `TodoReconciliation\Data\MembershipData`.
 *
 * This is deliberately NOT part of `Customizations`: customizations are
 * per-organization rules layered on top of a base sharing ministry plan,
 * while ministry identity and tier data are membership/organization columns.
 */
export interface MinistryContext {
  sharingMinistry: SharingMinistryType | null | undefined
  /**
   * The Sedera member's chosen Initial Unshareable Amount (IUA), from
   * `memberships.iua_amount`. Only set when the ministry is Sedera.
   */
  sederaIuaAmount?: number | null
  /**
   * The Samaritan Ministries member's plan level, from `memberships.smi_plan`.
   * Only set when the ministry is SMI. Determines the per-need IUA via
   * `getSmiPlanLimits`.
   */
  smiPlan?: SmiPlan | null
}

export function chooseSubmissionType(
  {
    incident,
    incidentSubmissions,
    incidentExpenses,
    allMembershipSubmissions,
    allMembershipIncidents,
  }: {
    incident: IncidentModel,
    incidentSubmissions: SubmissionModel[],
    incidentExpenses: ExpenseModel[],
    /**
     * Optional. Required to apply Sedera's annual 3-incident IUA cap.
     * If omitted, the cap is not applied and the per-membership IUA is used.
     */
    allMembershipSubmissions?: SubmissionModel[],
    /**
     * Optional. Used together with `allMembershipSubmissions` to assign each
     * Sedera submission to a calendar year (by its incident's `start_date`).
     */
    allMembershipIncidents?: IncidentModel[],
  },
  {
    hra,
    ministry,
  }: {
    hra: Customizations['hra'],
    ministry: MinistryContext,
  },
): SubmissionType | null {
  const { sharingMinistry, sederaIuaAmount, smiPlan } = ministry
  const hasCHM = sharingMinistry === 'CHM'
  const hasSedera = sharingMinistry === 'Sedera'
  const hasSMI = sharingMinistry === 'SMI'
  const limits = getCurrentLimits()
  // SMI's qualifying amount is the plan-level Initial Unshareable Amount
  // ($1,000 Classic / $2,000 Basic), applied per need. No annual cap — SMI has
  // no analog of Sedera's Annual Maximum Unshareable.
  const smiQualifyingAmount: Decimal | undefined = smiPlan ? getSmiPlanLimits(smiPlan).iuaAmount : undefined
  // Sedera's qualifying amount is per-membership (the chosen IUA), not a global limit.
  // The Annual Maximum Unshareable: once a member has paid their IUA on three separate
  // incidents in a calendar year, any subsequent need that year is fully shared from
  // dollar one — modelled here by treating the qualifying amount as 0.
  const sederaCapReached = hasSedera
    && countPriorSederaIncidentsInIncidentYear(incident, allMembershipSubmissions, allMembershipIncidents) >= 3
  const sederaQualifyingAmount: number | null | undefined = sederaCapReached ? 0 : sederaIuaAmount
  const priorSubmissions = incidentSubmissions.filter(isSubmitted)

  // If there are no expenses, we can't submit
  if (incidentExpenses.length === 0) {
    return null
  }

  // If there are any incomplete expenses, we can't submit yet
  if (incidentExpenses.some((e) => !isCompleteExpense(e))) {
    return null
  }

  const completeExpenses = incidentExpenses.filter(isCompleteExpense)

  // Expenses marked not shareable (e.g. maintenance/OTC medications) are not eligible for the
  // sharing ministry, so they must not count toward the CHM qualifying amount / Sedera IUA.
  // 'unknown' only sits on incomplete pharmacy drafts (filtered out above), but exclude it
  // explicitly as defense-in-depth so an unresolved expense can never inflate the sum.
  const shareableExpenses = completeExpenses.filter((e) =>
    e.shareability !== 'not_shareable' && e.shareability !== 'unknown')

  // If we have previously submitted to CHM (any CHM-related type), this is an add-on incident.
  // Don't gate the prior-submission check on hasCHM: if CHM submissions already exist, CHM is in play
  // even if sharing_ministry_type hasn't synced yet (e.g. old persisted Redux state).
  const hasPriorChmSubmissions = priorSubmissions.some((s) =>
    s.submission_type === 'CHM' || s.submission_type === 'CHM-addon' || s.submission_type === 'CHM-personal-responsibility')
  if (hasPriorChmSubmissions || (hasCHM && incident.is_addon_incident)) {
    return 'CHM-addon'
  }

  // If we have previously submitted to Sedera, this is a Sedera add-on: Sedera members upload
  // follow-on bills to their existing open Need in the member portal, sharing them from dollar
  // one once the IUA on the initial submission has been met — no qualifying-amount check here.
  // Don't gate on hasSedera, mirroring the CHM-addon stale-Redux defense above.
  const hasPriorSederaSubmissions = priorSubmissions.some((s) =>
    s.submission_type === 'Sedera' || s.submission_type === 'Sedera-addon')
  if (hasPriorSederaSubmissions || (hasSedera && incident.is_addon_incident)) {
    return 'Sedera-addon'
  }

  // If we have previously submitted to SMI, this is an SMI add-on: Samaritan members add
  // follow-on bills to their existing need via the Add-on Form or the Dashboard Needs app.
  // The per-need IUA was already applied to the initial submission, so no qualifying-amount
  // check here. Don't gate on hasSMI, mirroring the CHM-addon stale-Redux defense above.
  const hasPriorSmiSubmissions = priorSubmissions.some((s) =>
    s.submission_type === 'SMI' || s.submission_type === 'SMI-addon')
  if (hasPriorSmiSubmissions || (hasSMI && incident.is_addon_incident)) {
    return 'SMI-addon'
  }

  // If we have previously submitted to an HRA, we need to roll up those expenses because we might re-submit them to CHM.
  if (priorSubmissions.find((s) => s.submission_type === 'HRA')) {
    const sumListed = shareableExpenses.reduce((sum, expense) =>
      sum.plus(new Decimal(expense.listedAmount)), new Decimal(0))

    if (hasCHM && sumListed.greaterThanOrEqualTo(limits.qualifying_amount)) {
      return 'CHM'
    }

    if (hasSedera && sederaQualifyingAmount != null && sumListed.greaterThanOrEqualTo(sederaQualifyingAmount)) {
      return 'Sedera'
    }

    // Strictly greater than, unlike CHM/Sedera's >=: the SMI guidelines share only the
    // portion of a need that EXCEEDS the IUA, so a need at exactly the IUA has $0 shareable.
    if (hasSMI && smiQualifyingAmount != null && sumListed.greaterThan(smiQualifyingAmount)) {
      return 'SMI'
    }

    if (hra?.hasHra) {
      return 'HRA'
    } else {
      return null
    }
  }

  // If we have not previously submitted to an HRA, at this point there should be no prior submissions.
  // If there are (e.g. ministry submissions when hasCHM/hasSedera/hasSMI is false), we can't
  // determine a submission type.
  if (priorSubmissions.length > 0) {
    return null
  }

  const sumListed = shareableExpenses.reduce((sum, expense) =>
    sum.plus(new Decimal(expense.listedAmount)), new Decimal(0))

  if (hasCHM && sumListed.greaterThanOrEqualTo(limits.qualifying_amount)) {
    return 'CHM'
  }

  if (hasSedera && sederaQualifyingAmount != null && sumListed.greaterThanOrEqualTo(sederaQualifyingAmount)) {
    return 'Sedera'
  }

  // Strictly greater than — see the comment on the HRA roll-up branch above.
  if (hasSMI && smiQualifyingAmount != null && sumListed.greaterThan(smiQualifyingAmount)) {
    return 'SMI'
  }

  // If the remaining expenses are all paid with the HRA card, we don't need to resubmit them to the HRA
  if (completeExpenses.every((e) => e.paid_with_hra)) {
    return null
  }

  if (hra?.hasHra) {
    return 'HRA'
  } else {
    return null
  }
}

/**
 * Calculates the next submission due date for an incident.
 * This is 6 months from the date of the earliest expense that has not been previously submitted to CHM.
 *
 * Sedera shares the same 6-month-from-date-of-service window as CHM, so this
 * function intentionally drives the due date for Sedera submit todos too. The
 * Sedera submit todo's due date is the single signal that tells the member when
 * their submission window closes — Sedera's guidelines do not ordinarily share
 * bills submitted more than six months after the date of service.
 *
 * SMI reuses the same 6-month window deliberately, even though Samaritan's hard
 * limit is longer (bills submitted more than ONE YEAR after service are not
 * ordinarily shared): the guidelines urge submitting as soon as possible, and a
 * 6-month due date keeps the member comfortably inside the 1-year limit.
 *
 * The "exclude already-submitted expenses" filter covers all sharing-ministry
 * types: an add-on submission's due date is 6 months from the earliest
 * not-yet-submitted expense's date of service, matching Sedera's per-bill window.
 */
export function calculateDueDate({
  incident,
  incidentExpenses,
  incidentSubmissions,
  submissionType,
}: {
  incident: IncidentModel,
  incidentExpenses: ExpenseModel[],
  incidentSubmissions: SubmissionModel[],
  submissionType: SubmissionType,
}
) {
  const expenseIDsSubmittedToSharingMinistry = incidentSubmissions
    .filter((s) => s.submission_type === 'CHM' || s.submission_type === 'CHM-addon' ||
      s.submission_type === 'Sedera' || s.submission_type === 'Sedera-addon' ||
      s.submission_type === 'SMI' || s.submission_type === 'SMI-addon')
    .flatMap((s) => s.expense_ids)
  const expensesNotSubmittedToSharingMinistry = incidentExpenses
    .filter((e) => !expenseIDsSubmittedToSharingMinistry.includes(e.id))

  const earliestExpenseDate = expensesNotSubmittedToSharingMinistry.map((e) => e.date).filter(present).sort()[0]
  
  if (!earliestExpenseDate) {
    return null
  }

  // Medical bills must be submitted within six months from the date of service
  // https://www.chministries.org/media/5189/chmguidelines.pdf
  let dueDate = formatISO(addMonths(parseDateInTimeZone(earliestExpenseDate), 6), { representation: 'date' })

  // exception: if the pending submission is to the HRA, then the due date is no later than feb 15th of the following year
  if (submissionType === 'HRA') {
    const feb15OfnextYear = `${parseDateInTimeZone(earliestExpenseDate).getFullYear() + 1}-02-15`
    dueDate = [dueDate, feb15OfnextYear].sort()[0]
  }

  return dueDate
}

/**
 * Counts distinct prior incidents (excluding the current one) that have a
 * submitted Sedera submission and whose incident `start_date` falls in the same
 * calendar year as the current incident's `start_date`. Used to apply Sedera's
 * Annual Maximum Unshareable cap (three IUAs per year).
 *
 * Returns 0 if either `allMembershipSubmissions` or `allMembershipIncidents` is
 * not provided, or if the current incident has no `start_date`.
 */
export function countPriorSederaIncidentsInIncidentYear(
  incident: IncidentModel,
  allMembershipSubmissions: SubmissionModel[] | undefined,
  allMembershipIncidents: IncidentModel[] | undefined,
): number {
  if (!allMembershipSubmissions || !allMembershipIncidents || !incident.start_date) {
    return 0
  }
  const incidentYear = parseDateInTimeZone(incident.start_date).getFullYear()
  const incidentsById = new Map(allMembershipIncidents.map((i) => [i.id, i]))

  const priorSederaIncidentIds = new Set<string>()
  for (const s of allMembershipSubmissions) {
    if (!isSubmitted(s)) { continue }
    if (s.submission_type !== 'Sedera') { continue }
    if (s.incident_id === incident.id) { continue }
    const linkedIncident = incidentsById.get(s.incident_id)
    if (!linkedIncident?.start_date) { continue }
    if (parseDateInTimeZone(linkedIncident.start_date).getFullYear() !== incidentYear) { continue }
    priorSederaIncidentIds.add(s.incident_id)
  }
  return priorSederaIncidentIds.size
}
